// The rail's entries: every feature's `nav` in registry order (docs/CONTRACTS.md section 16), plus the component
// inventory at /kitchen-sink in non-production builds (the route itself exists in every build). An `ownerOnly` entry
// is left out of the rail and the phone menu unless the session is the owner's.
import { useQuery } from '@tanstack/react-query'
import { Component } from 'lucide-react'
import type { NavEntry, NavGroup } from '../features/feature.ts'
import { features } from '../features/index.ts'
import { sessionQuery } from '../lib/session.ts'
import { isActiveAmong, matches } from './navMatch.ts'

const KITCHEN_SINK: NavEntry = { to: '/kitchen-sink', label: 'nav.kitchenSink', icon: Component, group: 'system' }

export const navEntries: readonly NavEntry[] = [...features.flatMap((f) => f.nav), ...(import.meta.env.PROD ? [] : [KITCHEN_SINK])]
const notOwnerEntries: readonly NavEntry[] = navEntries.filter((e) => !e.ownerOnly)

/** The entries the rail and the phone menu show this session: `ownerOnly` ones only to the owner. */
export function useNavEntries(): readonly NavEntry[] {
  const { data } = useQuery(sessionQuery)
  return data?.kind === 'user' && data.me.role === 'owner' ? navEntries : notOwnerEntries
}

/** Rail groups in order; `system` sits at the bottom of the rail. */
export const NAV_GROUPS: readonly NavGroup[] = ['work', 'network', 'later']

const ALL_TOS = [...navEntries, KITCHEN_SINK].map((e) => e.to)

/** Screens with no rail entry of their own belong to a module (design finding D08): the touch ledger to Pipeline, waves to Lists. */
const MODULE_OF: readonly (readonly [string, string])[] = [
  ['/touches', '/pipeline'],
  ['/waves', '/lists'],
]
function asModule(pathname: string): string {
  const own = MODULE_OF.find(([prefix]) => matches(pathname, prefix))
  return own ? own[1] + pathname.slice(own[0].length) : pathname
}

export function isActive(pathname: string, to: string): boolean {
  return isActiveAmong(asModule(pathname), to, ALL_TOS)
}

/** The entry the current path belongs to (the longest matching prefix), for the breadcrumb and the phone title. */
export function entryFor(pathname: string): NavEntry | undefined {
  const at = asModule(pathname)
  return [...navEntries, KITCHEN_SINK]
    .filter((e) => matches(at, e.to))
    .sort((a, b) => b.to.length - a.to.length)[0]
}
